import { useState, useEffect } from 'react'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import axios from 'axios'
import { AcceptInvitationModal } from './AcceptInvitationModal'
import { useSearchParams } from 'react-router-dom'
import { useSnackbar } from 'contexts/SnackbarContext'
import { SnackbarType } from 'utils/snackbar'

interface PendingInvitation {
    invitationId: string
    projectName: string
    companyName: string
    role: number
    type: 'public' | 'private'
    expiresAt: string
    invitedBy: {
        name: string
        email: string
    }
    invitedAt: string
}

interface InvitationNotificationProps {
    isOpen: boolean
}

export const InvitationNotification = ({ isOpen }: InvitationNotificationProps) => {
    const [invitations, setInvitations] = useState<PendingInvitation[]>([])
    const [loading, setLoading] = useState(true)
    const [showAcceptModal, setShowAcceptModal] = useState(false)
    const [selectedInvitation, setSelectedInvitation] = useState<PendingInvitation | null>(null)
    const [searchParams, setSearchParams] = useSearchParams()
    const { showSnackbar } = useSnackbar()

    const fetchInvitations = async () => {
        try {
            const response = await axios.get('/application/invites')
            if (response.data.success) {
                setInvitations(response.data.data || [])
                return response.data.data || []
            }
            return []
        } catch (error) {
            console.error('Failed to fetch invitations:', error)
            return []
        } finally {
            setLoading(false)
        }
    }

    // Check for URL query parameters to auto-open the accept modal
    useEffect(() => {
        const checkUrlParams = async () => {
            const acceptInvitationId = searchParams.get('accept-invitation')
            const projectName = searchParams.get('project')

            if (acceptInvitationId && projectName) {
                // Clear the URL params
                setSearchParams({})

                // Fetch invitations and find the matching one
                const fetchedInvitations = await fetchInvitations()
                const matchingInvitation = fetchedInvitations.find(
                    (inv: PendingInvitation) => inv.invitationId === acceptInvitationId && inv.projectName === projectName
                )

                if (matchingInvitation) {
                    setSelectedInvitation(matchingInvitation)
                    setShowAcceptModal(true)
                }
            } else {
                fetchInvitations()
            }
        }

        checkUrlParams()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleReject = async (invitation: PendingInvitation) => {
        try {
            await axios.post('/application/reject-invitation', {
                invitationId: invitation.invitationId,
                projectName: invitation.projectName,
            })
            // Remove from list
            setInvitations((prev) => prev.filter((inv) => inv.invitationId !== invitation.invitationId))
        } catch (error) {
            console.error('Failed to reject invitation:', error)
            const message = axios.isAxiosError(error) ? error.response?.data?.message : null
            showSnackbar(SnackbarType.ERROR, message || 'Failed to reject invitation. Please try again.')
        }
    }

    const handleAcceptClick = (invitation: PendingInvitation) => {
        setSelectedInvitation(invitation)
        setShowAcceptModal(true)
    }

    const handleAcceptSuccess = () => {
        // Refresh invitations list
        fetchInvitations()
        setShowAcceptModal(false)
        setSelectedInvitation(null)
    }

    if (loading || invitations.length === 0) {
        return null
    }

    // Show only the first invitation
    const invitation = invitations[0]

    if (!isOpen) {
        return null
    }

    return (
        <>
            {/*
             * Compact invitation card sized to live inside the sidebar's narrow
             * column. Uses Tailwind utilities (no MUI Joy Card / sx) so the
             * width snaps to the rail and the buttons stay inside the bounds.
             * Padding is dialled down to `p-3` so the card doesn't dwarf the
             * footer rows above it.
             */}
            <div className="mb-2 rounded-2xl border border-blue-500/20 bg-blue-500/10 p-3 dark:border-blue-500/30 dark:bg-blue-500/15">
                <p className="break-words text-[13px] font-semibold leading-snug text-slate-900 dark:text-slate-100">
                    <span className="break-all">{invitation.invitedBy?.email || 'Someone'}</span> invited you to join
                    {invitation.companyName ? ` ${invitation.companyName}` : ' their team'}
                </p>
                <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">{new Date(invitation.invitedAt).toLocaleDateString()} • Invitation</p>
                <div className="mt-2 flex gap-1.5">
                    <button
                        type="button"
                        onClick={() => handleAcceptClick(invitation)}
                        aria-label="Accept invitation"
                        className="inline-flex h-7 flex-1 cursor-pointer items-center justify-center gap-1 rounded-lg bg-emerald-500 px-2 text-[12px] font-semibold text-white transition hover:bg-emerald-600 active:scale-95"
                    >
                        <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
                        Accept
                    </button>
                    <button
                        type="button"
                        onClick={() => handleReject(invitation)}
                        aria-label="Reject invitation"
                        className="inline-flex h-7 flex-1 cursor-pointer items-center justify-center gap-1 rounded-lg border border-red-300 bg-white px-2 text-[12px] font-semibold text-red-600 transition hover:bg-red-50 active:scale-95 dark:border-red-500/40 dark:bg-slate-900 dark:text-red-400 dark:hover:bg-red-500/10"
                    >
                        <CancelOutlinedIcon sx={{ fontSize: 14 }} />
                        Reject
                    </button>
                </div>
            </div>

            {selectedInvitation && (
                <AcceptInvitationModal
                    open={showAcceptModal}
                    onClose={() => {
                        setShowAcceptModal(false)
                        setSelectedInvitation(null)
                    }}
                    invitation={selectedInvitation}
                    onSuccess={handleAcceptSuccess}
                />
            )}
        </>
    )
}
